export const blogPosts = [
  {
    slug: "the-magic-behind-the-moments",
    title: "The Magic Behind the Moments: What Children Really Learn in the Pool",
    excerpt:
      "At Happy Tiddlers, every splash tells a story — and it's so much bigger than swimming. We lift the curtain on the confidence, connection and life skills quietly growing beneath the surface.",
    date: "2026-10-09",
    author: "Nicky · Head of Happy",
    readTime: "4 min read",
    category: "Child Development",
    cta: { label: "What to expect at your first lesson", to: "/how-it-works" },
    heroImage: `${process.env.PUBLIC_URL}/blog-magic-moments.jpg`,
    heroAlt: "A smiling toddler with their arms wrapped around their dad's shoulders in the pool",
    body: [
      {
        type: "lead",
        text: "At Happy Tiddlers, every splash tells a story — and it's so much bigger than swimming.",
      },
      {
        type: "p",
        text: "If you've ever watched your little one in the pool — eyes wide, arms reaching, tiny toes kicking — you'll know there's something special happening. Yes, they're learning water skills. Yes, they're becoming safer and more confident. But beneath the surface, there's a whole world of emotional, physical, and developmental magic unfolding.",
      },
      {
        type: "p",
        text: "Parents often tell us, “I didn't realise how much they learn in the water until I saw it.” This blog lifts the curtain on those hidden moments — the ones that shape your child far beyond the pool.",
      },
      { type: "h2", text: "Confidence that starts in the water and spreads everywhere" },
      {
        type: "p",
        text: "Confidence doesn't arrive in big leaps. It grows in tiny, joyful wins.",
      },
      {
        type: "lead",
        text: "The first bubble blown. The first float held for two seconds. The first brave reach for a toy just out of grasp.",
      },
      {
        type: "p",
        text: "These micro-moments build a child's belief in themselves. They learn that trying is safe, that progress is fun, and that they can do hard things with support. We see children who start shy and cautious transform into bold, beaming little explorers — and that confidence follows them into nursery, friendships, and everyday life.",
      },
      { type: "h2", text: "Water: the ultimate sensory playground" },
      {
        type: "p",
        text: "Warm water, gentle resistance, buoyancy, movement — the pool is a sensory wonderland. Every session supports:",
      },
      {
        type: "ul",
        items: [
          "Balance and coordination",
          "Body awareness",
          "Motor skill development",
          "Calming sensory input",
        ],
      },
      {
        type: "p",
        text: "For babies and toddlers, this environment is soothing, stimulating, and deeply developmental. It's one of the reasons children leave the pool relaxed, happy, and often ready for the best nap of the week.",
      },
      { type: "h2", text: "The parent–child bond: stronger with every splash" },
      {
        type: "p",
        text: "One of the most beautiful things about Happy Tiddlers is watching parents and children connect.",
      },
      {
        type: "p",
        text: "In the water, you're fully present. No phones. No distractions. Just you and your little one, sharing achievements, eye contact, and giggles.",
      },
      {
        type: "p",
        text: "These moments build secure attachment — the foundation for emotional resilience. When your child looks to you for reassurance, and you're right there cheering them on, it creates a sense of safety that lasts long after the session ends.",
      },
      {
        type: "image",
        src: `${process.env.PUBLIC_URL}/blog-magic-moments-mum.jpg`,
        alt: "A smiling mum supporting her baby at the poolside as the baby holds the edge and looks at the camera",
        caption: "Every session is full of tiny discoveries — and a parent close by to share them with.",
      },
      { type: "h2", text: "Life skills learned through play" },
      {
        type: "p",
        text: "Swimming lessons are secretly packed with life skills:",
      },
      {
        type: "ul",
        items: [
          "Listening and following instructions",
          "Turn-taking and patience",
          "Problem-solving",
          "Feeling safe and supported by familiar grown-ups beyond their parents",
          "Celebrating effort, not perfection",
        ],
      },
      {
        type: "p",
        text: "Children learn all of this in a joyful, pressure-free environment. It's learning disguised as fun — the best kind.",
      },
      { type: "h2", text: "The Happy Tiddlers team: quiet heroes in the background" },
      {
        type: "p",
        text: "Our instructors don't just teach swimming. They read confidence levels, adapt activities, create safety, and celebrate every tiny milestone.",
      },
      {
        type: "p",
        text: "They know when a child needs encouragement, when they need space, and when they're ready for the next challenge. They're experts in emotional safety as much as physical safety — and that's what makes Happy Tiddlers feel different.",
      },
      {
        type: "lead",
        text: "Parents often say, “You just get my child.” That's exactly what we aim for.",
      },
      { type: "h2", text: "Real moments from real families" },
      {
        type: "p",
        text: "Every week we see magic unfold: a toddler who finally puts their face in the water after weeks of watching. A baby who beams every time they hear the splash song. A parent who says, “I didn't think they'd do that today — I'm so proud.”",
      },
      {
        type: "p",
        text: "These stories are the heartbeat of Happy Tiddlers. They remind us why we do what we do.",
      },
      { type: "h2", text: "A gentle invitation" },
      {
        type: "p",
        text: "Whether you're already part of our Tiddlers family or thinking about joining, we're here to help your little one grow, splash, and shine. The pool is more than a place to learn to swim — it's a place where confidence, connection, and joy come alive.",
      },
      {
        type: "lead",
        text: "And we can't wait to share those moments with you.",
      },
    ],
  },
  {
    slug: "build-water-confidence-this-summer",
    title: "Helping Your Little Tiddler Build Water Confidence This Summer",
    excerpt:
      "Summer is the perfect time to build water confidence. Here are simple, pressure-free ways to help your little Tiddler feel safe, happy and adventurous in the water.",
    date: "2026-07-13",
    author: "Helen · Chief Tiddler",
    readTime: "3 min read",
    category: "Water Confidence",
    heroImage: `${process.env.PUBLIC_URL}/blog-summer-confidence.jpg`,
    heroAlt: "A happy, confident baby smiling while swimming in the pool",
    body: [
      {
        type: "lead",
        text: "Summer is the perfect time for children to enjoy the water. Whether it's a paddling pool in the garden, a trip to the beach, or a family holiday abroad, every splash is an opportunity to build confidence.",
      },
      {
        type: "p",
        text: "At Happy Tiddlers, we often remind parents that water confidence is the very first step in learning to swim. Before children can master front crawl, float independently or jump into deeper water, they first need to feel safe, secure and happy in the water.",
      },
      {
        type: "p",
        text: "Here are some simple ways you can help your little Tiddler build confidence over the summer.",
      },
      { type: "h2", text: "Let them find their feet" },
      {
        type: "p",
        text: "One of the best things you can do is let your child spend time in shallow water where they can comfortably touch the bottom (always under close adult supervision). Children are naturally more adventurous when they feel secure.",
      },
      {
        type: "lead",
        text: "I often say to parents: “Imagine I was a giant and I picked you up, carried you out into the middle of the ocean and said, ‘Go on, put your face under the water!’ You'd probably have something to say about that!”",
      },
      {
        type: "p",
        text: "That's exactly how many children feel when they're taken into water where they can't stand. When they know they can simply put their feet down whenever they need to, they're much more likely to splash, blow bubbles, jump, float and eventually put their face in the water — all because they feel safe enough to give it a go.",
      },
      { type: "h2", text: "Don't rush it" },
      {
        type: "p",
        text: "Every child develops confidence at their own pace. If your little Tiddler doesn't want to go underwater today, that's okay. If today they only want to sit on the step and splash their feet, that's okay too.",
      },
      {
        type: "p",
        text: "Confidence grows through positive experiences, not pressure. Celebrate the little wins, because they soon become the big ones.",
      },
      { type: "h2", text: "Play more than you practise" },
      {
        type: "p",
        text: "Children learn best through play. Instead of asking them to “practise swimming,” try:",
      },
      {
        type: "ul",
        items: [
          "Playing “Simon Says” in the water",
          "Blowing bubbles together",
          "Chasing floating toys",
          "Watering each other with watering cans",
          "Jumping into shallow water",
          "Making the biggest splash possible!",
        ],
      },
      {
        type: "p",
        text: "The more fun they have, the more positive memories they create around water.",
      },
      { type: "h2", text: "Choose the right equipment" },
      {
        type: "p",
        text: "Swimming aids can be helpful, but remember that not all equipment builds confidence in the same way. More importantly, no swimming aid should ever be used as a drowning prevention measure and should never replace close adult supervision.",
      },
      {
        type: "p",
        text: "A swim noodle or a well-fitting float jacket often allows children to move more naturally than an inflatable ring. If your child is happy standing in shallow water, try letting them spend some time without flotation aids, while you stay within arm's reach. Feeling the water around their body and learning how it supports them is a huge confidence builder.",
      },
      {
        type: "p",
        text: "Remember, no swimming aid replaces close adult supervision.",
      },
      { type: "h2", text: "Keep water safety fun" },
      {
        type: "p",
        text: "Summer is also the perfect time to teach simple water safety habits. Talk to your little Tiddler about:",
      },
      {
        type: "ul",
        items: [
          "Walking instead of running near water",
          "Always asking an adult before going near water",
          "Never swimming alone",
          "Why we don't lean over the edge of pools or lakes",
          "Wearing a buoyancy aid when paddleboarding or boating",
        ],
      },
      {
        type: "p",
        text: "Children learn best when these conversations are relaxed, regular and positive.",
      },
      { type: "h2", text: "Follow their lead" },
      {
        type: "p",
        text: "At Happy Tiddlers, we don't believe in forcing children through fear. We believe in following the child. That doesn't mean we stop encouraging them — it means we meet them where they are. We support them, celebrate their progress and allow them to explore when they're ready.",
      },
      {
        type: "p",
        text: "By doing this, we build something even more important than swimming skills — we build trust. Children who feel safe are far more likely to take positive risks than children who feel pressured.",
      },
      {
        type: "p",
        text: "After all, swimming isn't a race. Every little Tiddler has their own journey, and our job is to walk alongside them, encouraging them every step of the way.",
      },
      { type: "h2", text: "Finally" },
      {
        type: "p",
        text: "Confidence isn't built by taking away a child's sense of security. It's built by giving them just enough support that they feel ready to take the next small step themselves.",
      },
      {
        type: "p",
        text: "We hope you have a wonderful summer filled with laughter, splashing, and making memories together.",
      },
      {
        type: "lead",
        text: "Remember, every happy splash, every bubble blown and every tiny step forward is helping your little Tiddler build a lifelong love of the water.",
      },
    ],
  },
  {
    slug: "why-finishing-sharks-3-matters",
    title: "Why finishing Sharks 3 matters — the skills that could save a life",
    excerpt:
      "“My child can already swim — do they really need to finish Sharks 3?” Our answer is always yes. Here's why completing the journey matters more than ever.",
    date: "2026-05-27",
    author: "Helen · Chief Tiddler",
    readTime: "6 min read",
    category: "Water Safety",
    heroImage: `${process.env.PUBLIC_URL}/blog-family-beach.jpg`,
    heroAlt: "A family enjoying the sea together on a summer beach holiday",
    body: [
      {
        type: "lead",
        text: "At Happy Tiddlers, we often hear parents say: “My child can already swim — do they really need to finish Sharks 3?” Our answer is always the same: absolutely, yes. And sadly, recent events have shown just how important that is.",
      },
      {
        type: "p",
        text: "Over the past few weeks, several people have tragically lost their lives after getting into difficulty in open water during the hot weather. The heartbreaking truth is that many of them could swim.",
      },
      {
        type: "p",
        text: "What they often lacked wasn't the ability to move through the water — it was the knowledge, confidence and understanding of how to stay safe around it.",
      },
      {
        type: "p",
        text: "Open water holds dangers a pool never does: cold water shock, hidden currents, sudden changes in depth, exhaustion and panic can overwhelm even a strong swimmer in seconds. Knowing how to spot the risks, stay calm, conserve energy and make safe choices can matter just as much as being able to swim.",
      },
      { type: "h2", text: "Why water safety is personal to us" },
      {
        type: "p",
        text: "This isn't just a policy for us — it's personal. When our founder Helen was 11, she had a near-drowning experience. It happened in a swimming pool, not open water, and it stayed with her for life.",
      },
      {
        type: "p",
        text: "What makes it so telling is that Helen was already a strong, confident swimmer. And yet she still found herself in real difficulty. That day taught her a lesson she's never forgotten: being able to swim and being safe in the water are not the same thing.",
      },
      {
        type: "p",
        text: "For years afterwards she avoided going out of her depth, and the sea was out of the question entirely. The confidence she'd always had around water disappeared almost overnight.",
      },
      {
        type: "p",
        text: "When Helen had her son, she was determined he'd be safe around water, so she enrolled him in lessons. Watching him learn reminded her how much she loved the water — and rekindled her own confidence and passion. More than that, it cemented a belief that shapes everything we do: every child deserves not just to learn to swim, but to build a genuine love and respect for water, and the awareness to stay safe in it.",
      },
      { type: "h2", text: "The risk of stopping lessons too early" },
      {
        type: "p",
        text: "One of the biggest dangers in swimming is the gap between confidence and actual ability. A child can look confident in the water but still be missing the skills that keep them safe:",
      },
      {
        type: "ul",
        items: [
          "Stamina to keep going over a distance",
          "Reliable breathing control",
          "Survival and self-rescue skills",
          "True awareness of risk around water",
        ],
      },
      {
        type: "p",
        text: "Children who stop too early may tire quickly, panic if they can't stand, or overestimate what they can really do. Swimming a length simply isn't the same as being safe around water — real competence comes from knowing how to react when things don't go to plan.",
      },
      { type: "h2", text: "Our Sharks pathway — more than “just another badge”" },
      {
        type: "p",
        text: "Our Sharks programme follows the STA Learn to Swim pathway and is designed to grow children into safe, confident and capable swimmers, one gentle step at a time.",
      },
      {
        type: "pathway",
        items: [
          { label: "Sharks 1 — Building confidence & foundations", text: "Water confidence, safe entries and exits, floating and breath control, and early propulsion skills." },
          { label: "Sharks 2 — Developing independence", text: "Independent travel through the water, rotation skills, early stroke patterns and better breathing habits." },
          { label: "Sharks 3 — Developing safety & capability", text: "Greater stamina, stronger technique, water safety and survival skills, and real independence." },
        ],
      },
      {
        type: "image",
        src: `${process.env.PUBLIC_URL}/blog-sharks-matter.png`,
        alt: "Infographic explaining why completing Sharks 1, 2 and 3 matters, and the risks of leaving lessons early",
        caption: "Each stage builds on the last — completing all three is what turns a swimmer into a safe, capable one.",
      },
      { type: "h2", text: "Why finishing Sharks 3 is so important" },
      {
        type: "p",
        text: "Sharks 3 is a major milestone — and completing it means so much more than a certificate. It's where children learn the skills that could one day save their life.",
      },
      {
        type: "points",
        items: [
          { title: "Learning to tread water", text: "Staying afloat calmly, without constantly swimming, is one of the most important safety skills a child can have — helping them stay calm, conserve energy and stay safe." },
          { title: "Confidence out of their depth", text: "Many children feel confident only when they can stand. Even though most can touch the bottom of our pool, we gently encourage them to keep swimming — building real confidence, stamina and independence." },
          { title: "Early lifesaving skills", text: "Children learn controlled floating and lifesaving-style movements that help them conserve energy and stay safer in the water." },
          { title: "Building stamina", text: "Swimming a few metres is very different from swimming safely over a distance. Sharks 3 develops the endurance of a genuinely capable swimmer." },
        ],
      },
      {
        type: "image",
        src: `${process.env.PUBLIC_URL}/blog-float-to-live.png`,
        alt: "Five steps to know how to float — Float to Live water safety guide",
        caption: "Floating is a lifesaving skill we teach every swimmer: if you ever get into difficulty, Float to Live.",
      },
      {
        type: "p",
        text: "And nothing beats the feeling of graduating from Happy Tiddlers having completed every award — celebrating not just swimming ability, but the confidence, resilience and life skills built along the way.",
      },
      {
        type: "lead",
        text: "Because for us, swimming was never just a hobby. It's a love and respect for water, and a life skill that could one day save your life. Our goal isn't simply to teach children to swim — it's to help them be confident, capable and safe around water, for life.",
      },
    ],
  },
];

export const getPost = (slug) => blogPosts.find((p) => p.slug === slug);
